import { useState } from 'react';
import { DatePicker, Flex, Input, Tag } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useLazyExportTurnoverXlsxQuery, useTurnoverReportQuery } from '@/entities/reports';
import type { TurnoverReportRow } from '@/entities/reports';
import { ReportPage } from '@/shared/ui';
import type { ResponsiveColumns } from '@/shared/ui';
import { useBreakpoint, useDebouncedValue } from '@/shared/lib/hooks';
import { useStoreContext } from '@/shared/lib/store-context';
import { todayInTz, formatDate } from '@/shared/lib/date';

/** §13: обороты по категориям за период (stock_movements); товар ищется по подстроке названия. */
export function TurnoverReportPage() {
  const ctx = useStoreContext();
  const { isMobile } = useBreakpoint();
  const today = todayInTz(ctx.timezone);
  const defaultFrom = dayjs(today).startOf('month').format('YYYY-MM-DD');
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(today);
  const [search, setSearch] = useState('');
  const periodChanged = from !== defaultFrom || to !== today;
  const searchText = search.trim();
  // В запрос (и в экспорт) уходит «устоявшийся» поиск: таблица и файл всегда совпадают с тем, что на экране.
  const searchParam = useDebouncedValue(searchText) || undefined;
  const { data, isFetching } = useTurnoverReportQuery({ from, to, search: searchParam }, { pollingInterval: 30_000 });
  const [exportXlsx] = useLazyExportTurnoverXlsxQuery();

  const columns: ResponsiveColumns<TurnoverReportRow> = [
    { title: 'Товар', dataIndex: 'categoryName' },
    { title: 'Остаток на начало', dataIndex: 'openingQty', width: 140, align: 'right' },
    { title: 'Приход за период', dataIndex: 'incomingQty', width: 140, align: 'right' },
    { title: 'Расход за период', dataIndex: 'outgoingQty', width: 140, align: 'right' },
    { title: 'Остаток на конец', dataIndex: 'closingQty', width: 140, align: 'right' },
    { title: '', width: 120, mobile: 'subtitle', render: (_v, r) => (r.recalcPending ? <Tag color="processing">пересчитывается</Tag> : null) },
  ];

  return (
    <ReportPage
      title="Обороты"
      filters={
        <Flex vertical={isMobile} wrap={!isMobile} gap={isMobile ? 12 : 8} align={isMobile ? 'stretch' : 'center'}>
          <DatePicker.RangePicker
            format="DD.MM.YYYY"
            inputReadOnly={isMobile}
            value={[dayjs(from), dayjs(to)]}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                setFrom(dates[0].format('YYYY-MM-DD'));
                setTo(dates[1].format('YYYY-MM-DD'));
              }
            }}
          />
          <Input
            type={isMobile ? 'search' : undefined}
            size={isMobile ? 'large' : undefined}
            enterKeyHint="search"
            autoComplete="off"
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Поиск по товару"
            style={isMobile ? undefined : { width: 240 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Flex>
      }
      columns={columns}
      activeFilters={(periodChanged ? 1 : 0) + (searchText ? 1 : 0)}
      onResetFilters={() => {
        setFrom(defaultFrom);
        setTo(today);
        setSearch('');
      }}
      filtersSummary={`${formatDate(from)} — ${formatDate(to)}${searchText ? ` · «${searchText}»` : ''}`}
      data={data?.rows ?? []}
      loading={isFetching}
      rowKey="categoryId"
      onExport={() => exportXlsx({ from, to, search: searchParam }).unwrap()}
      exportFilename="Обороты.xlsx"
    />
  );
}
